// ─────────────────────────────────────────────────────────────────────────────
// Imports
// ─────────────────────────────────────────────────────────────────────────────

import Icon from '@ant-design/icons'
import {Avatar, Button, Input, Modal} from 'antd'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import {createValue} from 'components/form/utils'
import getProfileIdentifyingHint from 'helpers/getProfileIdentifyingHint'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useEffect, useRef, useState} from 'react'
import {AiOutlineQuestion} from 'react-icons/ai'
import {BsCheckLg, BsChevronDown, BsPlusLg, BsSearch, BsTag} from 'react-icons/bs'
import {FaUserTie} from 'react-icons/fa6'
import {IoCheckmarkCircle, IoClose} from 'react-icons/io5'
import {RiSearch2Line} from 'react-icons/ri'
import {useToggle} from 'react-use'
import {renderSelf} from 'views/Shared'
import {Pure} from '../../components/Pure'
import {API_ROOT_URL} from '../../envs/_current/config'
import preventParentEvent from '../../helpers/preventParentEvent'
import useAsync from '../../modules/asyncCache/useAsync'
import Translate from '../../modules/local/Translate'
import {useRequiredLogin} from '../../views/Server/useRequiredLogin'
import {useDeBounceValue} from '../../views/Search/useDeBounceValue'
import LoadingBlock from '../../views/Loading/LoadingBlock'
import {renderIf} from '../../views/Shared'

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const buildUrlWithParams = (pathUrl, keyword) => {
    const urlObj = new URL(pathUrl, window.location.origin)
    urlObj.searchParams.set('keyword', keyword)
    return urlObj.pathname + '?' + urlObj.searchParams.toString()
}

// ─────────────────────────────────────────────────────────────────────────────
// Default option renderer
// ─────────────────────────────────────────────────────────────────────────────

const renderOptDefault = (o, i) => {
    const identifyingHint = o._type === 'profile' ? getProfileIdentifyingHint(o, o.label) : null
    return (
        <Translate key={i}>
            {(t) => (
                <div className="flex items-center gap-3">
                    <Avatar
                        size={36}
                        src={o.avatar || o.cover_photo || o.thumbnail}
                        icon={<FaUserTie size={14} className="text-color-500"/>}
                        className="flex flex-center background-200 flex-shrink-0"
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                        {renderIf(
                            o.patron_saint_name,
                            <span className="text-2xs text-color-400 italic leading-tight">
                                {o.patron_saint_name}
                            </span>
                        )}
                        <div className="text-sm font-medium text-color-000 leading-snug truncate">
                            {t(o.label)}
                        </div>
                        {renderIf(
                            identifyingHint,
                            <span className="text-2xs text-color-400 italic leading-tight truncate">
                                {identifyingHint}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </Translate>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Default option row wrapper — flat, full-width clickable
// ─────────────────────────────────────────────────────────────────────────────

const OptWrapperDefault = ({children, selected, ...props}) => (
    <div
        className={classNames(
            'flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-300 lookup-item',
            selected
                ? 'bg-primary-50 shadow-sm ring-1 ring-primary-200'
                : 'hover:background-50'
        )}
        {...props}>
        {children}
    </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// LookupResult sub-components
// ─────────────────────────────────────────────────────────────────────────────

const CreateKeywordRow = ({keyword, type, onFinish, toggle}) => {
    const t = useTranslate()
    const handleClick = () => {
        onFinish(createValue(type, keyword || ''))
        toggle()
    }
    return (
        <div
            onClick={handleClick}
            className="flex items-center gap-3 px-3 py-2 rounded-lg background-50 border-l-2 border-secondary-400 mx-1 mb-1 cursor-pointer hover:background-100 transition-colors">
            <BsPlusLg size={13} className="text-secondary flex-shrink-0"/>
            <div className="flex flex-col flex-1 min-w-0">
                <span className="text-2xs text-color-400 italic leading-tight">
                    {t('keyword')}
                </span>
                <span className="font-medium text-sm text-secondary-600 leading-tight truncate">
                    {keyword}
                </span>
            </div>
            <span className="px-1 font-medium text-xs italic text-secondary hover:text-secondary-700 transition-colors cursor-pointer">
                {t('use this keyword')}
            </span>
        </div>
    )
}

const AliasEditor = ({selectedValue, displayValue, setDisplayValue, onFinish, toggle}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col gap-3 pt-3 border-t border-color-50">
            <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-color-300 uppercase tracking-wider">
                    {t('display name')}
                </span>
                <span className="text-xs text-color-400 italic leading-snug">
                    <span className="text-color-300">{`* ${t('note')}: `}</span>
                    {t('display name description')}
                </span>
            </div>
            <div className="flex items-center gap-3">
                <Input
                    size="large"
                    className="flex-1"
                    value={displayValue}
                    placeholder={t('no selected')}
                    onChange={(e) => setDisplayValue(e.target.value)}
                />
                <Button
                    size="large"
                    type="primary"
                    className="flex-shrink-0 button-rounded-md no-border px-6"
                    onClick={() => {
                        onFinish({
                            target: {
                                ...selectedValue,
                                value: displayValue || selectedValue.label || '',
                            },
                        })
                        toggle()
                    }}>
                    {t('apply')}
                </Button>
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// LookupResult — fetches & renders option list
// ─────────────────────────────────────────────────────────────────────────────

const LookupResult = ({
                          cacheRequired = true,
                          type,
                          keyword,
                          createIfNotExists,
                          withAlias,
                          toggle,
                          renderOption = renderOptDefault,
                          OptionWrapper = OptWrapperDefault,
                          onFinish,
                          apiPath = null,
                          schema = null,
                          selectedOption,
                          defaultDisplayValue,
                          valueType = 'id',
                          excludeIds = [],
                      }) => {
    const [displayValue, setDisplayValue] = useState(
        defaultDisplayValue || selectedOption?.value || getTitle(selectedOption)
    )
    const [selectedValue, setSelectedValue] = useState(
        selectedOption || {
            ...selectedOption,
            ...(valueType === 'id'
                ? {
                    label: selectedOption?.label || getTitle(selectedOption),
                    value: selectedOption?.id || getId(selectedOption),
                }
                : {
                    id: selectedOption?.value || displayValue,
                }),
        }
    )

    const isSelected = ({id, value}) => {
        if (!selectedValue?.id) return false
        return [id, value].includes(selectedValue.id)
    }

    const path = apiPath
        ? buildUrlWithParams(apiPath, keyword)
        : `/lookup/${type}/gets?keyword=${keyword}`

    const {response} = useAsync({
        ...(cacheRequired ? {cacheId: path} : {}),
        apiInfo: {
            root: API_ROOT_URL,
            path,
            method: 'GET',
            ...(schema ? {schema} : {}),
        },
    })

    let options = _.uniq(
        _.filter(_.get(response, 'data.data', []), (o) => o.id !== '').map((option) => ({
            ...option,
            ...(valueType === 'id'
                ? {
                    label: getTitle(option),
                    value: option?.id || getId(option),
                }
                : {}),
        }))
    )

    if (excludeIds.length > 0) {
        options = options.filter((o) => !excludeIds.includes(o.id))
    }

    if (selectedOption?.id) {
        const found = options.some((el) => el.id === selectedOption.id)
        if (!found) {
            options.push({
                ...selectedOption,
                label: selectedOption?.label || getTitle(selectedOption),
                value: selectedOption?.id || getId(selectedOption),
            })
        }
        options = _.sortBy(options, (obj) => obj.id !== selectedOption.id)
    }

    const handleSelect = (o) => {
        onFinish({
            target: {
                ...o,
                value: o.label || '',
                id: o.value,
            },
        })
        toggle()
    }

    const handleSelectWithAlias = (o) => {
        setDisplayValue(o.label)
        setSelectedValue({
            ...o,
            value: o.label || '',
            id: o.value,
        })
    }

    return (
        <div key={keyword} className="w-full flex flex-col gap-1">
            {createIfNotExists && keyword?.length > 0 && (
                <CreateKeywordRow
                    keyword={keyword}
                    type={type}
                    onFinish={onFinish}
                    toggle={toggle}
                />
            )}

            {options.length > 0 && (
                <div
                    style={{maxHeight: withAlias && selectedOption?.id ? 300 : 380}}
                    className="flex flex-col gap-2 overflow-y-auto scrollBarThin pr-1 transition-all duration-300 lookup-field-options">
                    {options.map((o) => {
                        const active = isSelected(o)
                        return (
                            <OptionWrapper
                                key={o.value}
                                selected={active}
                                onClick={() => {
                                    if (!withAlias) {
                                        handleSelect(o)
                                    } else {
                                        handleSelectWithAlias(o)
                                    }
                                }}>
                                {renderOption(o)}
                                {active && (
                                    <BsCheckLg
                                        size={18}
                                        className="flex-shrink-0 text-primary-500 mx-3"
                                    />
                                )}
                            </OptionWrapper>
                        )
                    })}
                </div>
            )}

            {withAlias && selectedValue?.id && (
                <AliasEditor
                    selectedValue={selectedValue}
                    displayValue={displayValue}
                    setDisplayValue={setDisplayValue}
                    onFinish={onFinish}
                    toggle={toggle}
                />
            )}
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Lookup — modal shell
// ─────────────────────────────────────────────────────────────────────────────

const LookupSearchInput = ({inputRef, onSubmit, onChange, defaultValue}) => {
    const t = useTranslate()
    return (
        <Input
            ref={inputRef}
            autoFocus
            allowClear
            size="large"
            className="rounded-lg"
            defaultValue={defaultValue}
            placeholder={t('enter your keyword')}
            prefix={<BsSearch size={13} className="text-color-400 mr-1"/>}
            onPressEnter={onSubmit}
            onChange={onChange}
        />
    )
}

const Lookup = ({
                    isToggle,
                    toggle,
                    withAlias,
                    createIfNotExists,
                    type,
                    title,
                    description = 'lookup modal description',
                    onFinish,
                    apiPath = null,
                    selectedOption,
                    defaultDisplayValue = null,
                    ...props
                }) => {
    const t = useTranslate()
    const inputSearchRef = useRef(null)
    const [searchTerm, setSearchTerm] = useState('')
    const debouncedSearchTerm = useDeBounceValue(searchTerm, 300)

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (inputSearchRef.current) inputSearchRef.current.focus()
        }, 300)
        return () => clearTimeout(timeout)
    }, [])

    const handleSearchChange = (e) => {
        preventParentEvent(e)
        setSearchTerm(e?.target?.value || '')
    }

    const handleSearchSubmit = () => {
        onFinish(createValue(type, searchTerm || ''))
        toggle()
    }

    return (
        <Modal
            width={560}
            className="custom-modal lookup-modal"
            maskClosable={false}
            destroyOnHidden
            open={isToggle}
            onCancel={toggle}
            footer={null}
            title={
                <div className="text-center font-bold text-color-000 tracking-wider">
                    {title ? t(title) : t('select')}
                </div>
            }>
            <div className="flex flex-col gap-3">
                {description && (
                    <div className="px-3 py-2.5 flex flex-col gap-1 rounded-xl background-50 border border-color-50">
                        <div className="flex items-center gap-1">
                            <RiSearch2Line size={13} className="text-gray-600 flex-shrink-0"/>
                            <span className="font-medium text-gray-600 underline">{`${t('note')}:`}</span>
                        </div>
                        <span className="text-color-300 italic leading-relaxed whitespace-pre-line">
                            {t(description)}
                        </span>
                    </div>
                )}

                <LookupSearchInput
                    inputRef={inputSearchRef}
                    defaultValue={defaultDisplayValue}
                    onSubmit={handleSearchSubmit}
                    onChange={handleSearchChange}
                />

                <Pure
                    key={debouncedSearchTerm}
                    input={[debouncedSearchTerm, selectedOption]}>
                    <LookupResult
                        type={type}
                        createIfNotExists={createIfNotExists}
                        withAlias={withAlias}
                        keyword={debouncedSearchTerm}
                        toggle={toggle}
                        onFinish={onFinish}
                        apiPath={apiPath}
                        selectedOption={selectedOption}
                        defaultDisplayValue={defaultDisplayValue}
                        {...props}
                    />
                </Pure>

                <div className="flex justify-end pt-2 border-t border-color-50">
                    <Button onClick={toggle} className="border-none no-shadow">
                        {t('close')}
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// LookupField — inline trigger button
// ─────────────────────────────────────────────────────────────────────────────

const SelectingPlaceholder = ({label}) => (
    <div
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-color-200 background text-color-400 text-sm italic">
        {label}
    </div>
)

const LookupField = ({
                         search = true,
                         name,
                         bordered,
                         defaultValue,
                         selectedOption,
                         disabled = false,
                         createIfNotExists = true,
                         withAlias = false,
                         placeholder,
                         handleChange,
                         apiPath = null,
                         renderEnterButton,
                         getValue = renderSelf,
                         title,
                         className,
                         withInput = () => ({}),
                         ...props
                     }) => {
    const t = useTranslate()
    const [isToggle, toggle] = useToggle(false)

    const displayVal = getValue(defaultValue)
    const hasValue = !!displayVal
    const isLinked = !!selectedOption?.id

    if (isToggle) {
        return (
            <React.Fragment>
                <SelectingPlaceholder label={t('selecting...')}/>
                <Lookup
                    title={title}
                    type={name}
                    isToggle={isToggle}
                    toggle={toggle}
                    withAlias={withAlias}
                    selectedOption={selectedOption}
                    createIfNotExists={createIfNotExists}
                    onFinish={handleChange}
                    apiPath={apiPath}
                    defaultDisplayValue={defaultValue}
                    {...props}
                />
            </React.Fragment>
        )
    }

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && toggle(true)}
            // style={{height: 40}}
            className={classNames(
                'flex items-center gap-2 w-full px-3 py-1 rounded-lg border transition-colors text-left group',
                disabled
                    ? 'border-color-50 background-100 cursor-not-allowed'
                    : 'border-color-100 background hover:border-secondary-500 cursor-pointer',
                className
            )}>
            {/* Prefix icon: linked from DB / typed keyword / empty */}
            {!hasValue
                ? <RiSearch2Line size={14} className="text-color-400 flex-shrink-0 group-hover:text-secondary transition-colors"/>
                : isLinked
                    ? <IoCheckmarkCircle size={15} className="text-green-500 flex-shrink-0"/>
                    : <BsTag size={13} className="text-secondary-500 flex-shrink-0"/>
            }

            {/* Display value or placeholder */}
            <span className={classNames(
                'flex-1 truncate',
                hasValue ? 'text-color-700 font-medium' : 'text-color-400 italic'
            )}>
                {hasValue
                    ? t(displayVal)
                    : (placeholder
                        ? t(placeholder)
                        : t(createIfNotExists ? 'click to select / create' : 'click to select'))
                }
            </span>

            {/* Suffix: clear or chevron */}
            {hasValue ? (
                <span
                    role="button"
                    className="p-1 flex flex-shrink-0 text-color-300 hover:text-primary-600 transition-colors duration-300 -mr-1"
                    onClick={(e) => {
                        e.stopPropagation()
                        handleChange(createValue(name, null))
                    }}>
                    <IoClose size={14}/>
                </span>
            ) : (
                <BsChevronDown
                    size={12}
                    className="flex flex-shrink-0 text-color-400 hover:text-primary transition-colors duration-300"
                />
            )}
        </button>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// LookupComponent — action-button variant
// ─────────────────────────────────────────────────────────────────────────────

export const LookupComponent = ({
                                    name,
                                    defaultValue,
                                    isLoading = false,
                                    createIfNotExists = true,
                                    handleChange,
                                    apiPath = null,
                                    title,
                                    description,
                                    renderActionButton,
                                    isLoginRequired = false,
                                    ...props
                                }) => {
    const t = useTranslate()
    const handleRequiredLogin = useRequiredLogin()
    const [isToggle, toggle] = useToggle(false)

    const handleOpen = () => {
        if (isLoginRequired) {
            handleRequiredLogin(toggle)
        } else {
            toggle()
        }
    }

    if (isLoading) {
        return <LoadingBlock/>
    }

    if (isToggle) {
        return (
            <React.Fragment>
                <div className="px-1 py-3 text-color-400 italic text-sm">
                    {t('selecting...')}
                </div>
                <Lookup
                    title={title}
                    description={description}
                    type={name}
                    isToggle={isToggle}
                    toggle={toggle}
                    selectedOption={defaultValue}
                    createIfNotExists={createIfNotExists}
                    onFinish={handleChange}
                    apiPath={apiPath}
                    {...props}
                />
            </React.Fragment>
        )
    }

    return (
        <div onClick={handleOpen}>
            {renderActionButton
                ? renderActionButton()
                : <Icon component={AiOutlineQuestion}/>
            }
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// useLookupField — hook variant
// ─────────────────────────────────────────────────────────────────────────────

export const useLookupField = ({
                                   name,
                                   title,
                                   handleChange,
                                   defaultValue,
                                   apiPath = null,
                                   createIfNotExists = true,
                                   ...props
                               }) => {
    const [isToggle, toggle] = useToggle(false)

    const modal = (
        <Lookup
            type={name}
            title={title}
            toggle={toggle}
            apiPath={apiPath}
            isToggle={isToggle}
            onFinish={handleChange}
            selectedOption={defaultValue}
            createIfNotExists={createIfNotExists}
            {...props}
        />
    )

    return {toggle, isToggle, modal}
}

// ─────────────────────────────────────────────────────────────────────────────
// Export
// ─────────────────────────────────────────────────────────────────────────────

export default LookupField